import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    Table,
    Drawer,
    Form,
    Input,
    Select,
    InputNumber,
    Switch,
    Space,
    AutoComplete,
    Tag,
    Popconfirm,
    Tabs,
    Checkbox,
    Empty,
} from "antd";
import {
    SafetyCertificateOutlined,
    UserOutlined,
    AppstoreOutlined,
    CheckCircleOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import TitleHead from "../components/Customs/TitleHead";
import UserPermissionTab from "./UserPermissionTab";

import { GeneralService } from "../Services/GeneralService";
import { notify } from "../components/Customs/NotifyProvider";
import { formatDate } from "../components/Customs/dateFormatter";

import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from "../../utils/permissionUtils";
import { useNavigate } from 'react-router-dom';

const MODULE_KEY = "user_access";


const UserAccess = () => {

    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();
    const [data, setData] = useState([]);
    const categoryOptions = [
        { value: "Food" },
        { value: "Laundry" },
        { value: "Transport" },
        { value: "Housekeeping" },
        { value: "Luggage Handling" },
        { value: "Taxi / Pickup" },
        { value: "AC / Heater Charges" },
        { value: "Extra Bed" },
        { value: "Other" },
    ];
    const [isEditing, setIsEditing] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    const [options, setOptions] = useState(categoryOptions);
    const [searchText, setSearchText] = useState("");

    const getAllServices = async () => {
        try {
            const res = await GeneralService.get("users");
            console.log("res.data", res.data);

            setData(
                (res.data || []).map((item, index) => ({
                    si_no: index + 1,
                    ...item,
                }))
            );
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        getAllServices();
    }, [])

    const filteredData = data.filter((item) => {
        const value = searchText.toLowerCase();

        return Object.values(item).some((field) => {
            if (field === null || field === undefined) return false;
            return field.toString().toLowerCase().includes(value);
        });
    });

    const handleSearch = (value) => {
        if (!value) {
            setOptions(categoryOptions);
        } else {
            const filtered = categoryOptions.filter((item) =>
                item.value.toLowerCase().includes(value.toLowerCase())
            );
            setOptions(filtered);
        }
    };

    // Table Columns
    const columns = [
        {
            title: "S. No.",
            dataIndex: "si_no",
            align: "center",
        },
        {
            title: "User Name",
            dataIndex: "name",
        },
        {
            title: "E-mail",
            dataIndex: "email",
        },
        {
            title: "Active",
            dataIndex: "is_active",
            render: (val) => (val ? <Tag color="green">Active</Tag> : <Tag color="red">In-Active</Tag>),
        },
        {
            title: "Actions",
            render: (_, record) => (
                <Space>
                    {canUpdateModule(MODULE_KEY) && (
                        <Button type="link" onClick={() => handleEdit(record)}>
                            Edit
                        </Button>
                    )}
                    {canDeleteModule(MODULE_KEY) && (
                        <Popconfirm
                            title="Delete Record"
                            description="Are you sure you want to delete this item?"
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(record)}
                        >
                            <Button danger type="link">
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        }
    ];

    const handleEdit = (record) => {
        setIsEditing(true);
        setEditingRecord(record);
        setOpen(true);

        form.setFieldsValue({
            ...record,
        });
    };

    const handleDelete = async (record) => {
        try {
            const res = await GeneralService.delete("users", record.id);

            if (res.status) {
                notify.success("User deleted");
                setData((prev) => prev.filter((item) => item.id !== record.id));
            } else {
                notify.error("Delete failed");
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Open Drawer
    const showDrawer = () => {
        setOpen(true);
    };

    // Close Drawer
    const onClose = () => {
        setOpen(false);
        form.resetFields();
        setIsEditing(false);
        setEditingRecord(null);
    };

    // Save Form
    const onFinish = async (values) => {
        const newData = {
            ...values,
            updated_at: formatDate(),
        };

        try {
            if (isEditing) {
                // UPDATE
                const req = await GeneralService.update(
                    "users",
                    editingRecord.id,
                    newData

                );

                if (req.status) {
                    notify.success("User updated successfully");

                    getAllServices();

                    setIsEditing(false);
                    setEditingRecord(null);
                    onClose();
                } else {
                    notify.error("Update failed");
                    getAllServices();
                }
            } else {
                // CREATE
                const req = await GeneralService.create("users", {
                    ...newData,
                    created_at: formatDate(),
                });

                if (req.status) {
                    notify.success("User created successfully");
                    setData((prev) => [...prev, req.data]);
                    onClose();
                    getAllServices();
                } else {
                    notify.error("Create failed");
                    getAllServices();
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const userManageTab = (
        <Card
            title={
                <TitleHead
                    h1='User Access Management'
                    h2='Manage user permissions, login controls, and account activity overview.'
                />
            }
            extra={
                <Space>
                    <Input.Search
                        size="large"
                        placeholder="Search User..."
                        allowClear
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 340 }}
                    />

                    {canCreateModule(MODULE_KEY) && (
                        <Button size="large" type="primary" onClick={showDrawer}>
                            Create User
                        </Button>
                    )}
                </Space>
            }
        >
        <Table columns={columns} dataSource={filteredData}
            pagination={{
                showSizeChanger: true,
                // showQuickJumper: true,
                showTotal: (total) => `Total ${total} records`,
            }} />

            {/* Drawer */}
            <Drawer
                title={isEditing ? "Edit User" : "Create User"}
                width={400}
                open={open}
                onClose={onClose}
            >
                <Form layout="vertical" form={form} onFinish={onFinish}>
                    <Form.Item
                        label="User Name"
                        name="name"

                    >
                        <Input />
                    </Form.Item>




                    <Form.Item label="E-mail" name="email"
                        rules={[{ required: true, message: "Enter user name" }]}
                    >
                        <Input placeholder="example@gmail.com" />
                    </Form.Item>

                    <Form.Item
                        label="Active"
                        name="is_active"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>


                    <Space>
                        <Button type="primary" htmlType="submit">
                            Save
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                    </Space>
                </Form>
            </Drawer>
        </Card>
    );

    if (!canViewModule(MODULE_KEY)) {
        return <Empty description="No permission to view this page" />;
    }

    return (
        <Card bodyStyle={{ padding: 0 }}>
            <Tabs
                defaultActiveKey="1"
                size="large"
                style={{ padding: "0 24px" }}
                onChange={(key) => {
                    if (key === "2") navigate("/user-permissions");
                }}
                items={[
                    { key: "1", label: "Manage Users", children: userManageTab },
                    { key: "2", label: "User Permissions", children: null },
                ]}
            />
        </Card>
    );
};

export default UserAccess;
